import { useState, useRef, useCallback } from "react";
import { showErrorToast } from "../utils/toasts";

export interface UseImageUploadOptions {
    maxSizeMB?: number;
    acceptedTypes?: string[];
    onError?: (error: string) => void;
    returnAsFile?: boolean; // If true, returns File object; if false, returns base64 string
}

export interface UseImageUploadReturn {
    selectedFile: File | null;
    preview: string | null;
    base64Image: string | null;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleRemove: () => void;
    handleButtonClick: () => void;
    clear: () => void;
    setFile: (file: File | null) => void;
    setImage: (base64: string | null) => void;
}

const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_ACCEPTED_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
];

const DEFAULT_ACCEPTED_EXTENSIONS = [ ".png", ".jpg", ".jpeg" ];

/**
 * Reusable hook for image upload functionality
 * Handles file selection, validation, preview generation, and conversion
 */
export function useImageUpload(
    options: UseImageUploadOptions = {}
): UseImageUploadReturn {
    const {
        maxSizeMB = DEFAULT_MAX_SIZE_MB,
        acceptedTypes = DEFAULT_ACCEPTED_TYPES,
        onError,
        returnAsFile = false,
    } = options;

    const [ selectedFile, setSelectedFile ] = useState<File | null>(null);
    const [ preview, setPreview ] = useState<string | null>(null);
    const [ base64Image, setImage ] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleError = useCallback(
        (message: string) => {
            if (onError) {
                onError(message);
            } else {
                showErrorToast(message);
            }
        },
        [ onError ]
    );

    const validateFile = useCallback(
        (file: File): boolean => {
            // Validate file type
            const isValidType =
                acceptedTypes.includes(file.type) ||
                DEFAULT_ACCEPTED_EXTENSIONS.some((ext) =>
                    file.name.toLowerCase().endsWith(ext)
                );

            if (!isValidType) {
                handleError(
                    `Please select a valid image file (${acceptedTypes.join(", ")})`
                );
                return false;
            }

            // Validate file size
            const maxSize = maxSizeMB * 1024 * 1024;
            if (file.size > maxSize) {
                handleError(`Image size must be less than ${maxSizeMB}MB`);
                return false;
            }

            return true;
        },
        [ acceptedTypes, maxSizeMB, handleError ]
    );

    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[ 0 ];
            if (!file) return;

            if (!validateFile(file)) {
                // Reset input
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                return;
            }

            setSelectedFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                const dataUrl = reader.result as string;
                setPreview(dataUrl);

                // If returnAsFile is false, extract base64 data
                if (!returnAsFile) {
                    const base64Data = dataUrl.includes(",")
                        ? dataUrl.split(",")[ 1 ]
                        : dataUrl;
                    setImage(base64Data);
                } else {
                    setImage(null);
                }
            };
            reader.onerror = () => {
                handleError("Failed to read image file");
                setSelectedFile(null);
                setPreview(null);
                setImage(null);
            };
            reader.readAsDataURL(file);
        },
        [ validateFile, returnAsFile, handleError ]
    );

    const handleRemove = useCallback(() => {
        setSelectedFile(null);
        setPreview(null);
        setImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);

    const handleButtonClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const clear = useCallback(() => {
        handleRemove();
    }, [ handleRemove ]);

    const setFile = useCallback(
        (file: File | null) => {
            if (!file) {
                handleRemove();
                return;
            }

            if (!validateFile(file)) {
                return;
            }

            setSelectedFile(file);

            const reader = new FileReader();
            reader.onloadend = () => {
                const dataUrl = reader.result as string;
                setPreview(dataUrl);

                if (!returnAsFile) {
                    const base64Data = dataUrl.includes(",")
                        ? dataUrl.split(",")[ 1 ]
                        : dataUrl;
                    setImage(base64Data);
                } else {
                    setImage(null);
                }
            };
            reader.onerror = () => {
                handleError("Failed to read image file");
            };
            reader.readAsDataURL(file);
        },
        [ validateFile, returnAsFile, handleError, handleRemove ]
    );

    const setImageExternal = useCallback(
        (base64: string | null) => {
            if (!base64) {
                handleRemove();
                return;
            }

            // If it's a data URL, extract base64 and set preview
            if (base64.startsWith("data:")) {
                setPreview(base64);
                const base64Data = base64.includes(",")
                    ? base64.split(",")[ 1 ]
                    : base64;
                setImage(base64Data);
            } else {
                // If it's just base64, create data URL for preview
                const dataUrl = `data:image/png;base64,${base64}`;
                setPreview(dataUrl);
                setImage(base64);
            }
            // Clear selectedFile since we're setting from base64
            setSelectedFile(null);
        },
        [ handleRemove ]
    );

    return {
        selectedFile,
        preview,
        base64Image,
        fileInputRef,
        handleFileSelect,
        handleRemove,
        handleButtonClick,
        clear,
        setFile,
        setImage: setImageExternal,
    };
}

